import { useEffect } from 'react'
import {
  absoluteUrl,
  DEFAULT_SOCIAL_IMAGE,
  SEO_PAGES,
  SITE_NAME,
  type SeoPageKey,
} from '../config/seo'

type StructuredData = Record<string, unknown> | Array<Record<string, unknown>>

type SEOProps = {
  page: SeoPageKey
  title?: string
  description?: string
  canonical?: string
  robots?: string
  image?: string
  imageAlt?: string
  type?: 'website' | 'article'
  structuredData?: StructuredData
}

function setMeta(attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.append(element)
  }
  element.content = content
}

function setOptionalVerification(name: string, content?: string) {
  const selector = `meta[name="${name}"]`
  const existing = document.head.querySelector<HTMLMetaElement>(selector)
  if (!content) {
    existing?.remove()
    return
  }
  setMeta('name', name, content)
}

export default function SEO({
  page,
  title,
  description,
  canonical,
  robots,
  image = DEFAULT_SOCIAL_IMAGE,
  imageAlt = 'Diamond Suites Ocala logo beside the reception lounge with equestrian artwork',
  type = 'website',
  structuredData,
}: SEOProps) {
  const defaults = SEO_PAGES[page]
  const resolvedTitle = title || defaults.title
  const resolvedDescription = description || defaults.description
  const resolvedCanonical = absoluteUrl(canonical || defaults.path)
  const resolvedRobots = robots || ('robots' in defaults ? defaults.robots : 'index, follow')
  const resolvedImage = absoluteUrl(image)

  useEffect(() => {
    document.title = resolvedTitle
    setMeta('name', 'description', resolvedDescription)
    setMeta('name', 'robots', resolvedRobots)
    setMeta('property', 'og:type', type)
    setMeta('property', 'og:site_name', SITE_NAME)
    setMeta('property', 'og:locale', 'en_US')
    setMeta('property', 'og:title', resolvedTitle)
    setMeta('property', 'og:description', resolvedDescription)
    setMeta('property', 'og:url', resolvedCanonical)
    setMeta('property', 'og:image', resolvedImage)
    setMeta('property', 'og:image:alt', imageAlt)
    setMeta('property', 'og:image:width', '1200')
    setMeta('property', 'og:image:height', '630')
    setMeta('name', 'twitter:card', 'summary_large_image')
    setMeta('name', 'twitter:title', resolvedTitle)
    setMeta('name', 'twitter:description', resolvedDescription)
    setMeta('name', 'twitter:image', resolvedImage)
    setMeta('name', 'twitter:image:alt', imageAlt)

    let canonicalLink = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!canonicalLink) {
      canonicalLink = document.createElement('link')
      canonicalLink.rel = 'canonical'
      document.head.append(canonicalLink)
    }
    canonicalLink.href = resolvedCanonical

    setOptionalVerification(
      'google-site-verification',
      import.meta.env.VITE_GOOGLE_SITE_VERIFICATION?.trim(),
    )
    setOptionalVerification(
      'msvalidate.01',
      import.meta.env.VITE_BING_SITE_VERIFICATION?.trim(),
    )

    const existingSchema = document.getElementById('route-structured-data')
    if (!structuredData) {
      existingSchema?.remove()
      return
    }

    const schemaScript = existingSchema || document.createElement('script')
    schemaScript.id = 'route-structured-data'
    schemaScript.setAttribute('type', 'application/ld+json')
    schemaScript.textContent = JSON.stringify(structuredData).replace(/</g, '\\u003c')
    if (!existingSchema) document.head.append(schemaScript)
  }, [
    imageAlt,
    resolvedCanonical,
    resolvedDescription,
    resolvedImage,
    resolvedRobots,
    resolvedTitle,
    structuredData,
    type,
  ])

  return null
}
